import React, { ReactElement } from 'react';
import {
  DropdownButton,
  MenuItemProps,
  TabsHeaderProps,
} from '@odin-labs/components';
import { NewHeader } from 'components/header/NewHeader';
import { WrenchIcon, PlusIcon, UserIcon, IdBadgeIcon } from 'components/icons';
import { AuthContext } from 'auth';
import { to } from 'acl';
import { ensureNonEmptyItems } from 'utils';
import { HeaderAction } from 'components/header/types';
import { Jobsite } from 'containers/jobsite/types';
import { useTempBadgeModal } from 'containers/jobsite/tabs/jobsiteTempBadges/modals/tempBadgeModal/components';
import { JobsiteTitle } from './JobsiteTitle';

export interface JobsiteHeaderProps {
  jobsite: Jobsite;
  baseRoute: string;
  tabsProps?: TabsHeaderProps;
  onReloadPressed?: () => void;
  openAddContractorAssignmentModal: () => void;
  openAddJobsiteUserModal: () => void;
}

export function JobsiteHeader(props: JobsiteHeaderProps): ReactElement {
  const {
    jobsite,
    baseRoute,
    tabsProps,
    onReloadPressed,
    openAddContractorAssignmentModal,
    openAddJobsiteUserModal,
  } = props;
  const { jobsiteId } = jobsite ?? {};
  const { currentUser: user } = React.useContext(AuthContext);
  const { openModal: openTempBadgeModal } = useTempBadgeModal();

  const menuItems = ensureNonEmptyItems<MenuItemProps>([
    user.isAllowed(to.addContractorJobsiteAssignments) && {
      onClick: openAddContractorAssignmentModal,
      text: 'Contractor Assignment',
      icon: WrenchIcon,
    },
    user.isAllowed(to.addJobsiteUsers, jobsiteId) && {
      onClick: openAddJobsiteUserModal,
      text: 'User',
      icon: UserIcon,
    },
    user.isAllowed(to.manageTempBadges, jobsiteId) && {
      onClick: (): void =>
        openTempBadgeModal({ modalAction: 'createAndPrint' }),
      text: 'Temporary Badge',
      icon: IdBadgeIcon,
    },
  ]);

  return (
    <NewHeader
      title={
        <JobsiteTitle
          name={jobsite?.name}
          addressCity={jobsite?.addressCity}
          addressState={jobsite?.addressState}
          organizationName={jobsite?.developer?.organization?.name}
          profileImageUrl={jobsite?.profilePicture?.downloadUrl}
        />
      }
      actionsProps={{
        baseRoute,
        onReloadPressed,
        headerActions: ensureNonEmptyItems<HeaderAction>([
          'back',
          'reload',
          user.isAllowed(to.editJobsiteConfiguration, jobsiteId)
            ? 'settings'
            : undefined,
        ]),
        children: menuItems.length > 0 && (
          <DropdownButton menuItems={menuItems} icon={PlusIcon} text="Add" />
        ),
      }}
      tabsProps={tabsProps}
    />
  );
}
